import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { leagueAPI, uploadAPI, userAPI, clubAPI } from '../services/api';
import MapPicker from '../components/MapPicker';
import { useAlert } from '../components/Alert';
import './CreateLeague.css';

const formatDate = (date) => date.toISOString().split('T')[0];

const getDefaultStartDate = () => {
  const date = new Date();
  date.setDate(date.getDate() + 7);
  return formatDate(date);
};

const getDefaultEndDate = (startDateString) => {
  const baseDate = startDateString ? new Date(startDateString) : new Date();
  const date = new Date(baseDate);
  date.setDate(date.getDate() + 21);
  return formatDate(date);
};

const CreateLeague = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin } = useAuth();
  const { showAlert } = useAlert();
  const [canCreateLeague, setCanCreateLeague] = useState(true);
  const [limitMessage, setLimitMessage] = useState('');
  const [maxActiveLeagues, setMaxActiveLeagues] = useState(null);
  const [checkingLimit, setCheckingLimit] = useState(true);
  const [previousLeague, setPreviousLeague] = useState(null);
  const [loadingPreviousLeague, setLoadingPreviousLeague] = useState(false);
  const [clubs, setClubs] = useState([]);
  const [loadingClubs, setLoadingClubs] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    sport: 'Tennis (singles)',
    minLevel: 0,
    maxLevel: 100,
    startDate: getDefaultStartDate(),
    endDate: getDefaultEndDate(getDefaultStartDate()),
    latitude: null,
    longitude: null,
    matchFormat: 'best-of-3-sets-to-6',
    leagueType: 'individual',
    isSuperLeague: false,
    maxPlayersPerSubLeague: 8,
    clubId: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [coverPhotoFile, setCoverPhotoFile] = useState(null);

  // Load previous league data if creating a continuation
  useEffect(() => {
    const continuationData = location.state?.continuationFrom;
    if (continuationData?.leagueId) {
      setLoadingPreviousLeague(true);
      const loadPreviousLeague = async () => {
        try {
          const response = await leagueAPI.getLeague(continuationData.leagueId);
          const prevLeague = response.data;
          setPreviousLeague(prevLeague);
          
          // Calculate new dates (same logic as backend: 14 days from now for start, same duration)
          const newStartDate = new Date();
          newStartDate.setHours(0, 0, 0, 0);
          newStartDate.setDate(newStartDate.getDate() + 14);
          
          const prevStartDate = prevLeague.startDate ? new Date(prevLeague.startDate) : null;
          const prevEndDate = prevLeague.endDate ? new Date(prevLeague.endDate) : null;
          const durationMs = prevStartDate && prevEndDate && !Number.isNaN(prevStartDate.getTime()) && !Number.isNaN(prevEndDate.getTime())
            ? Math.max(0, prevEndDate.getTime() - prevStartDate.getTime())
            : null;
          
          const newEndDate = durationMs
            ? new Date(newStartDate.getTime() + durationMs)
            : null;
          
          // Pre-populate form with previous league settings
          setFormData({
            name: prevLeague.name || '',
            location: prevLeague.location || '',
            sport: prevLeague.sport || 'Tennis (singles)',
            minLevel: prevLeague.minLevel ?? 0,
            maxLevel: prevLeague.maxLevel ?? 100,
            startDate: formatDate(newStartDate),
            endDate: newEndDate ? formatDate(newEndDate) : getDefaultEndDate(formatDate(newStartDate)),
            latitude: prevLeague.latitude ?? null,
            longitude: prevLeague.longitude ?? null,
            matchFormat: prevLeague.matchFormat || 'best-of-3-sets-to-6',
            leagueType: prevLeague.isSuperLeague ? 'super' : 'individual',
            isSuperLeague: prevLeague.isSuperLeague || false,
            maxPlayersPerSubLeague: prevLeague.maxPlayersPerSubLeague ?? 8,
            clubId: prevLeague.clubId || '',
          });
        } catch (err) {
          console.error('Failed to load previous league:', err);
          showAlert('Failed to load previous league data', 'error');
        } finally {
          setLoadingPreviousLeague(false);
        }
      };
      
      loadPreviousLeague();
    }
  }, [location.state, showAlert]);

  useEffect(() => {
    // Load clubs where user is admin (for optional club selection)
    const loadClubs = async () => {
      if (!user) return;
      setLoadingClubs(true);
      try {
        const res = await clubAPI.getMyClubs();
        const allClubs = res.data || [];
        const adminClubs = allClubs.filter((club) => club.isAdmin);
        setClubs(adminClubs);
      } catch (err) {
        console.error('Failed to load clubs for league creation:', err);
      } finally {
        setLoadingClubs(false);
      }
    };

    loadClubs();
  }, [user]);

  useEffect(() => {
    // When creating a continuation and clubs are loaded, default club to previous league's clubId (if any)
    if (previousLeague && previousLeague.clubId && !formData.clubId && clubs.length > 0) {
      setFormData((prev) => ({
        ...prev,
        clubId: previousLeague.clubId,
      }));
    }
  }, [previousLeague, clubs, formData.clubId]);

  useEffect(() => {
    const checkActiveLeaguesLimit = async () => {
      if (!user || isAdmin) {
        // Global admins are not limited
        setCanCreateLeague(true);
        setLimitMessage('');
        setCheckingLimit(false);
        return;
      }

      setCheckingLimit(true);
      try {
        const userResponse = await userAPI.getUser(user.userId);
        const userData = userResponse.data;
        const maxActive = userData?.maxActiveLeagues ?? 1;
        setMaxActiveLeagues(maxActive);

        const createdLeaguesResponse = await userAPI.getCreatedLeagues(user.userId);
        const createdLeagues = createdLeaguesResponse.data || [];

        const now = new Date();
        // Set time to end of day for proper date comparison
        now.setHours(23, 59, 59, 999);
        
        const activeLeagues = createdLeagues.filter(league => {
          // Exclude subleagues - only count standalone leagues and superleagues
          if (league.superLeagueId) return false;
          // For continuation, exclude the previous league from active count
          if (previousLeague && league.leagueId === previousLeague.leagueId) return false;
          // League is active if it has no endDate or endDate is in the future (or today)
          if (!league.endDate) return true;
          const endDate = new Date(league.endDate);
          if (Number.isNaN(endDate.getTime())) return true; // Treat invalid dates as active
          // Set endDate to end of day for comparison
          endDate.setHours(23, 59, 59, 999);
          return endDate >= now;
        });

        console.log('Active leagues check:', {
          userId: user.userId,
          maxActiveLeagues: maxActive,
          activeLeaguesCount: activeLeagues.length,
          activeLeagues: activeLeagues.map(l => ({ id: l.leagueId, name: l.name, endDate: l.endDate })),
          allCreatedLeagues: createdLeagues.map(l => ({ id: l.leagueId, name: l.name, endDate: l.endDate }))
        });

        if (activeLeagues.length >= maxActive) {
          setCanCreateLeague(false);
          setLimitMessage(`You have reached your limit of ${maxActive} active league(s).`);
        } else {
          setCanCreateLeague(true);
          setLimitMessage('');
        }
      } catch (err) {
        console.error('Failed to check active leagues limit:', err);
        // On error, allow creation (backend will enforce the limit)
        setCanCreateLeague(true);
        setLimitMessage('');
      } finally {
        setCheckingLimit(false);
      }
    };

    if (user) {
      checkActiveLeaguesLimit();
    } else {
      setCheckingLimit(false);
    }
  }, [user, isAdmin, previousLeague]);

  const handleCoverPhotoChange = (e) => {
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    setCoverPhotoFile(file);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedForm = { ...formData, [name]: value };
    
    // When sport changes, update match format to default for that sport
    if (name === 'sport') {
      if (value === 'Squash') {
        updatedForm.matchFormat = 'squash-best-of-5-games-to-11';
      } else {
        updatedForm.matchFormat = 'best-of-3-sets-to-6';
      }
    }
    
    setFormData(updatedForm);
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    let updatedForm = { ...formData, [name]: value };

    if (name === 'startDate' && value) {
      // If start date moves beyond current end date (or no end date), push end date three weeks out
      if (!updatedForm.endDate || new Date(value) > new Date(updatedForm.endDate)) {
        updatedForm = {
          ...updatedForm,
          endDate: getDefaultEndDate(value),
        };
      }
    }

    setFormData(updatedForm);
    if (typeof e.target?.blur === 'function') {
      requestAnimationFrame(() => e.target.blur());
    }
  };

  const handleLeagueTypeChange = (e) => {
    const leagueType = e.target.value;
    setFormData({ 
      ...formData, 
      leagueType,
      isSuperLeague: leagueType === 'super'
    });
  };

  const handleLocationSelect = (position) => {
    setFormData({
      ...formData,
      latitude: position.lat,
      longitude: position.lng,
    });
  };

  const handleClubChange = (e) => {
    const { value } = e.target;
    const selectedClubId = value || '';
    const selectedClub = clubs.find((c) => c.clubId === selectedClubId);

    setFormData((prev) => {
      // When a club with coordinates is selected, move the league
      // latitude/longitude (and map pin) to the club's coordinates.
      if (
        selectedClub &&
        typeof selectedClub.latitude === 'number' &&
        typeof selectedClub.longitude === 'number'
      ) {
        return {
          ...prev,
          clubId: selectedClubId,
          latitude: selectedClub.latitude,
          longitude: selectedClub.longitude,
        };
      }

      return {
        ...prev,
        clubId: selectedClubId,
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.name) {
      showAlert('Name is required', 'warning');
      return;
    }

    const minLevel = parseInt(formData.minLevel, 10);
    const maxLevel = parseInt(formData.maxLevel, 10);
    const maxPlayersPerSubLeague = formData.maxPlayersPerSubLeague !== null && formData.maxPlayersPerSubLeague !== undefined
      ? parseInt(formData.maxPlayersPerSubLeague, 10)
      : null;

    if (Number.isNaN(minLevel) || minLevel < 0) {
      showAlert('Min level must be a number of at least 0', 'warning');
      return;
    }

    if (Number.isNaN(maxLevel) || maxLevel > 100) {
      showAlert('Max level must be a number of at most 100', 'warning');
      return;
    }

    if (minLevel >= maxLevel) {
      showAlert('Min level must be less than max level', 'warning');
      return;
    }

    if (formData.startDate && formData.endDate && new Date(formData.startDate) > new Date(formData.endDate)) {
      showAlert('Start date cannot be after end date', 'warning');
      return;
    }

    if (formData.isSuperLeague) {
      if (Number.isNaN(maxPlayersPerSubLeague)) {
        showAlert('Max players per subleague must be a number', 'warning');
        return;
      }

      if (maxPlayersPerSubLeague < 2) {
        showAlert('Max players per subleague is required and must be at least 2 for super leagues', 'warning');
        return;
      }
    } else if (formData.maxPlayersPerSubLeague) {
      if (Number.isNaN(maxPlayersPerSubLeague)) {
        showAlert('Max players per subleague must be a number', 'warning');
        return;
      }

      if (maxPlayersPerSubLeague < 2) {
        showAlert('Max players per subleague must be at least 2', 'warning');
        return;
      }
    }

    setLoading(true);

    try {
      let response;
      
      // If creating a continuation, use the continuation endpoint
      if (previousLeague) {
        response = await leagueAPI.createContinuationLeague(previousLeague.leagueId);
      } else {
        response = await leagueAPI.createLeague(formData);
      }
      
      const { leagueId } = response.data;
      
      // Optional: upload cover photo then update league with keys (only for new leagues, not continuations)
      if (!previousLeague && coverPhotoFile) {
        try {
          const uploadRes = await uploadAPI.uploadLeagueLogo(coverPhotoFile, leagueId);
          await leagueAPI.updateLeague(leagueId, {
            coverPhotoKey: uploadRes.data.key,
            coverPhotoUrl: uploadRes.data.url,
          });
        } catch (upErr) {
          console.warn('Cover photo upload failed:', upErr);
        }
      }
      
      if (formData.isSuperLeague) {
        navigate(`/super-leagues/${leagueId}`);
      } else {
        navigate(`/leagues/${leagueId}`);
      }
      
      if (previousLeague) {
        showAlert('Follow on league created successfully.', 'success');
      }
    } catch (err) {
      console.error('Create league error:', err);
      setError(err.response?.data?.error || 'Failed to create league');
    } finally {
      setLoading(false);
    }
  };

  // Show loading state while checking limit
  if (checkingLimit) {
    return (
      <div className="page-container">
        <div className="container">
          <div className="loading">Checking league limit...</div>
        </div>
      </div>
    );
  }

  // Show warning page if limit is reached
  if (!canCreateLeague && limitMessage) {
    return (
      <div className="page-container">
        <div className="container">
          <div className="page-header">
            <h1 className="page-title">Create League</h1>
          </div>
          <div className="card" style={{ 
            marginTop: '24px',
            padding: '32px',
            textAlign: 'center',
            backgroundColor: '#fff3cd',
            border: '2px solid #ffc107',
            borderRadius: '8px'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
            <h2 style={{ margin: '0 0 16px 0', fontSize: '1.5rem', color: '#856404' }}>
              League Limit Reached
            </h2>
            <p style={{ margin: '0 0 16px 0', fontSize: '1.1rem', color: '#856404', lineHeight: '1.6' }}>
              {limitMessage}
            </p>
            <p style={{ margin: '16px 0', fontSize: '1rem', color: '#856404', lineHeight: '1.6' }}>
              Please wait for one of your active leagues to complete before starting a new one.
            </p>
            <p style={{ margin: '16px 0 24px 0', fontSize: '1rem', color: '#856404', lineHeight: '1.6' }}>
              If you need to create more leagues, you can{' '}
              <a 
                href="/support" 
                style={{ color: '#0284c7', textDecoration: 'underline', fontWeight: '600' }}
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/support');
                }}
              >
                contact support
              </a>
              {' '}to request a limit increase.
            </p>
            <button
              type="button"
              onClick={() => navigate('/leagues')}
              className="btn btn-primary"
            >
              Back to Leagues
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="container">
        <div className="page-header">
          <h1 className="page-title">Create League</h1>
          <p className="page-subtitle">Build a new competition and welcome players to the court</p>
        </div>

        {error && <div className="error-message">{error}</div>}

        {/* Follow on League Banner */}
        {previousLeague && (
          <div className="card" style={{ 
            marginBottom: '24px', 
            backgroundColor: '#f0f9ff', 
            border: '2px solid #0284c7',
            borderRadius: '8px',
            padding: '16px 20px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <span style={{ fontSize: '24px' }}>🔄</span>
              <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600', color: '#0284c7' }}>
                Creating Follow on League
              </h2>
            </div>
            <div style={{ color: '#0c4a6e', lineHeight: '1.6' }}>
              <p style={{ margin: '4px 0' }}>
                <strong>Previous League:</strong> {previousLeague.name}
              </p>
              <p style={{ margin: '8px 0 0 0', fontSize: '0.9rem', opacity: 0.8 }}>
                The form below is pre-populated with settings from the previous league. You can modify any fields before creating the follow on league.
              </p>
            </div>
          </div>
        )}

        {loadingPreviousLeague && (
          <div className="card" style={{ marginBottom: '24px', textAlign: 'center', padding: '24px' }}>
            <div className="loading">Loading previous league settings...</div>
          </div>
        )}

        <div className="create-league-card card">
          <form onSubmit={handleSubmit} className="create-league-form">
            <div className="form-row three-columns">
              <div className="form-group">
                <label className="form-label">League Name *</label>
                <input
                  type="text"
                  name="name"
                  className="form-input"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g., Downtown Tennis League"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Sport *</label>
                <select
                  name="sport"
                  className="form-input sport-select"
                  value={formData.sport}
                  onChange={handleChange}
                  required
                >
                  <option value="Tennis (singles)">🎾 Tennis (singles)</option>
                  <option value="Tennis (doubles)" disabled>🎾 Tennis (doubles) (Coming Soon)</option>
                  <option value="Padel" disabled>🏓 Padel (Coming Soon)</option>
                  <option value="Squash">🏸 Squash</option>
                  <option value="Football" disabled>⚽ Football (Coming Soon)</option>
                  <option value="Basketball" disabled>🏀 Basketball (Coming Soon)</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">League Logo</label>
                <input
                  type="file"
                  accept="image/*"
                  className="form-input"
                  onChange={handleCoverPhotoChange}
                />
                {coverPhotoFile && (
                  <small className="form-hint">Selected file: {coverPhotoFile.name}</small>
                )}
                <small className="form-hint">
                  Upload an image to display as the league logo across the app. Square images work best.
                </small>
              </div>
            </div>

            <div className="form-row two-columns">
              <div className="form-group">
                <label className="form-label">Start Date *</label>
                <input
                  type="date"
                  name="startDate"
                  className="form-input"
                  value={formData.startDate}
                  onChange={handleDateChange}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">End Date *</label>
                <input
                  type="date"
                  name="endDate"
                  className="form-input"
                  value={formData.endDate}
                  onChange={handleDateChange}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Club (optional)</label>
              <select
                name="clubId"
                className="form-input"
                value={formData.clubId || ''}
                onChange={handleClubChange}
                disabled={loadingClubs || !!previousLeague}
              >
                <option value="">
                  {previousLeague ? 'Same as previous league (cannot change)' : 'No club (public league)'}
                </option>
                {clubs.map((club) => (
                  <option key={club.clubId} value={club.clubId}>
                    {club.name}
                  </option>
                ))}
              </select>
              <small className="form-hint">
                Select a club you administer to group this league under it. Leave empty to create a standalone public league.
              </small>
            </div>

            <div className="form-group">
              <label className="form-label">Select Location on Map *</label>
              <MapPicker
                onLocationSelect={handleLocationSelect}
                initialLocation={
                  formData.latitude && formData.longitude
                    ? { lat: formData.latitude, lng: formData.longitude }
                    : null
                }
              />
            </div>

            <div className="form-section-title">Skill Level Requirements</div>

            <div className="form-row two-columns">
              <div className="form-group">
                <label className="form-label">Min Level *</label>
                <input
                  type="number"
                  name="minLevel"
                  className="form-input"
                  value={formData.minLevel}
                  onChange={handleChange}
                  min="0"
                  max="100"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Max Level *</label>
                <input
                  type="number"
                  name="maxLevel"
                  className="form-input"
                  value={formData.maxLevel}
                  onChange={handleChange}
                  min="0"
                  max="100"
                  required
                />
              </div>
            </div>

            <div className="form-section-title">League Settings</div>

            <div className="form-row two-columns">
              <div className="form-group">
                <label className="form-label">League Type *</label>
                <select
                  name="leagueType"
                  className="form-input"
                  value={formData.leagueType}
                  onChange={handleLeagueTypeChange}
                  required
                >
                  <option value="individual">Standard League</option>
                  <option value="super">Super League (auto split into divisions)</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Match Format *</label>
                <select
                  name="matchFormat"
                  className="form-input"
                  value={formData.matchFormat}
                  onChange={handleChange}
                  required
                >
                  {formData.sport === 'Squash' ? (
                    <>
                      <option value="squash-best-of-5-games-to-11">Best of 5 Games (First to 11, win by 2)</option>
                      <option value="squash-best-of-3-games-to-11">Best of 3 Games (First to 11, win by 2)</option>
                    </>
                  ) : (
                    <>
                  <option value="best-of-3-sets-to-6">Best of 3 Sets (Sets to 6)</option>
                  <option value="best-of-3-sets-to-4">Best of 3 Sets (Sets to 4)</option>
                  <option value="best-of-3-deciding-tiebreak-sets-to-6">Best of 3 with 3rd Set Tiebreak to 10 (Sets to 6)</option>
                  <option value="best-of-3-deciding-tiebreak-sets-to-4">Best of 3 with 3rd Set Tiebreak to 10 (Sets to 4)</option>
                  <option value="single-set">Single Set</option>
                    </>
                  )}
                </select>
                <small className="form-hint">
                  This format will be used for all matches in the league and cannot be changed once matches are played
                </small>
              </div>
            </div>

            {formData.leagueType === 'super' && (
              <div className="form-group">
                <label className="form-label">Max Players per Subleague *</label>
                <input
                  type="number"
                  name="maxPlayersPerSubLeague"
                  className="form-input"
                  value={formData.maxPlayersPerSubLeague}
                  onChange={handleChange}
                  min="2"
                  max="50"
                  required={formData.isSuperLeague}
                />
                <small className="form-hint">
                  Players will be ranked by level and automatically split into subleagues with this maximum size
                </small>
              </div>
            )}

            <div className="form-actions">
              <button 
                type="submit" 
                className="btn btn-primary" 
                disabled={loading} 
              >
                {loading 
                  ? (previousLeague ? 'Creating Follow on League...' : 'Creating League...') 
                  : (previousLeague ? 'Create Follow on League' : 'Create League')}
              </button>
              <button
                type="button"
                onClick={() => navigate('/leagues')}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateLeague;

